import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ContractorListEnum, PersonaType, SkillType } from '@constants/global.enum';
import {
  Contractors,
  ContractorsBySkills,
} from '@interfaces/vendor-management-model';
import { GlobalErrorService } from '@services/global-error.service';
import { DataService } from '@services/data.service';
import { getVendorOptions } from '../vendor-management-filter/vendor-management-filter.helper';
import { dropdownValues } from '@interfaces/common.model';
import { dropdownOptions } from '@app/shared/interfaces/vendor.model';
import { filterData } from '@utils/utils';
import dayjs from 'dayjs';
import { yearDateFormat } from '@constants/dateFormat';

@Component({
  selector: 'app-contractor-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    EmeraldModalModule,
    EmeraldDropdownModule,
    EmeraldButtonModule,
  ],
  templateUrl: './contractor-filter.component.html',
  styleUrl: './contractor-filter.component.scss',
})
export class ContractorFilterComponent {
  @Input() fieldsVisibility: string = '';
  @Input() contractorDataByOrgView: Contractors[] = [];
  @Input() ContractorDataBySkills: ContractorsBySkills[] = [];
  @Output() filterApplied: EventEmitter<any> = new EventEmitter();
  @Output() closeModal = new EventEmitter<void>();
  @Output() filterReset = new EventEmitter<void>();
  opened: boolean = false;
  filterForm: FormGroup = new FormGroup({});
  personaType = PersonaType;
  skillType = SkillType;
  openForm: boolean = true;
  primarySkillOptions: dropdownValues[] = [];
  vendorOptions: dropdownValues[] = [];
  monthOptions: dropdownOptions[] = [];
  selectedMonth!: number;
  selectedYear!: number;
  selectedOption: string = '';

  constructor(
    public globalError: GlobalErrorService,
    private dataService: DataService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.initForm();
    this.generateMonthOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['contractorDataByOrgView'] &&
      changes['contractorDataByOrgView'].currentValue
    ) {
      this.loadFormData();
    }
  }

  initForm() {
    this.filterForm = this.fb.group({
      primarySkill: [[]],
      vendorName: [null],
      month: [null],
    });
  }

  generateMonthOptions(): void {
    const today = new Date();
    this.monthOptions = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
      };
    });
  }

  apply() {
    const filters = this.getFiltersFromForm();
    const filtered = this.filterOpenPositions(filters);

    this.filterApplied?.emit(filtered);
    this.closeModal?.emit();
  }

  private getFiltersFromForm() {
    const getValue = (field: string) =>
      this.filterForm.get(field)?.value ?? null;

    let primarySkill = getValue(this.skillType.PrimarySkill);
    if (Array.isArray(primarySkill) && typeof primarySkill[0] === 'object') {
      primarySkill = primarySkill
        .filter((skill: any) => skill?.selected)
        .map((skill: any) => skill.value);
    } else {
      primarySkill = null;
    }

    let dateOfJoining = getValue(ContractorListEnum.Month);

    if (typeof dateOfJoining === 'string') {
      const date = dayjs(dateOfJoining, yearDateFormat.date, true);
      if (date.isValid()) {
        dateOfJoining = {
          label: date.format(yearDateFormat.label),
          value: date.format(yearDateFormat.value),
          selected: true,
        };
      }
    }

    return {
      primarySkill,
      vendorName: getValue(ContractorListEnum.VendorName),
      filterByMonth: dateOfJoining,
    };
  }

  private filterOpenPositions(filters: any) {
    if (Array.isArray(filters.primarySkill)) {
      filters.primarySkill = filters.primarySkill
        .map((skill: any) => (typeof skill === 'string' ? skill.trim() : ''))
        .filter(Boolean);
      if (filters.primarySkill.length === 0) filters.primarySkill = null;
    }

    const processedData = (this.contractorDataByOrgView ?? []).map((item) => ({
      ...item,
      filterByMonth: item.dateOfJoining,
      primarySkill: Array.isArray(item.primarySkill)
        ? item.primarySkill.map((s: string) => s.trim())
        : typeof item.primarySkill === 'string'
          ? item.primarySkill.split(',').map((s) => s.trim())
          : [],
    }));

    if (filters.primarySkill) {
      return processedData
        .filter((item) =>
          filters.primarySkill.some((skill: string) =>
            item.primarySkill.includes(skill),
          ),
        )
        .filter(
          (item) =>
            filterData([item], { ...filters, primarySkill: null }).length > 0,
        );
    }

    return filterData(processedData, filters);
  }

  openDropdowns = true;

  reset() {
    this.filterForm.reset({
      primarySkill: [],
      vendorName: null,
    });
    this.filterApplied.emit(this.contractorDataByOrgView);
    this.openDropdowns = false;
    setTimeout(() => {
      this.openDropdowns = true;
    });
  }

  loadFormData() {
    let uniqueValues: (key: string) => string[] = () => [];
    if (
      this.contractorDataByOrgView &&
      this.contractorDataByOrgView?.length > 0
    ) {
      uniqueValues = (key: string) => [
        ...new Set(this.contractorDataByOrgView?.map((item: any) => item[key])),
      ];
    } else if (
      this.ContractorDataBySkills &&
      this.ContractorDataBySkills?.length > 0
    ) {
      uniqueValues = (key: string) => [
        ...new Set(this.ContractorDataBySkills?.map((item: any) => item[key])),
      ];
    }
    const allSkills: string[] = [];
    uniqueValues(this.skillType.PrimarySkill)?.forEach((value) => {
      if (value) {
        value?.split(',')?.forEach((skill) => {
          const trimmed = skill?.trim();
          if (trimmed) allSkills?.push(trimmed);
        });
      }
    });
    const uniqueSkills = Array?.from(new Set(allSkills));
    this.primarySkillOptions = uniqueSkills?.map((skill) => ({
      label: skill,
      value: skill,
    }));

    this.vendorOptions = getVendorOptions(this.dataService.vendors());
  }
}
